<template>
  <div class="project">
    <!-- Navbar -->
    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
        <img id="navbar-logo" src="../../assets/projects/smartmobile/smartMobile.jpg" alt="SmartMobile Logo">
      </div>
    </nav>

    <!-- Week 1: Embarking on a Technological Odyssey -->
    <section>
      <h1>Week 1: Embarking on a Technological Odyssey</h1>
      <p>
        Welcome to an exhilarating voyage into the fascinating realm of technology. In this week, we'll dive into thought-provoking theses that challenge conventional wisdom and spark our imagination.
      </p>
      <p>
        Picture this: <strong>Thesis 73</strong> invites us to contemplate technology as an art form, skillfully arranging the world to shield us from its challenges. "Convenience is our way of life."
      </p>
      <p>
        Now, let's take a playful detour to <strong>Thesis 38</strong>, likening tech giants to Orcas hunting startups like baby seals. "Tech giants are the titans of our time."
      </p>
      <p>
        <strong>Thesis 45</strong> ushers us into the debate of Google as a memory prosthesis for humanity, raising the question: Should it be considered a public utility? "We've entrusted our memory to Google."
      </p>
      <p>
        But beware, for in the maze of technology, <strong>Thesis 64</strong> warns us about the perils of the 'Internet of Things,' a potential security and privacy minefield. "IoT is not for the faint of heart."
      </p>
      <p>
        In our quest for understanding, always keep in mind <strong>Kranzberg's First Law</strong>: "Technology, like magic, is not inherently good or evil, but a potent force awaiting our command."
      </p>
      <p>
        And don't overlook the 'Right To Be Forgotten,' which might simply be the right not to be discovered by a search engine. "Data, once out there, has a life of its own."
      </p>
    </section>

    <!-- Brainstorming a Tech Odyssey -->
    <section>
      <h2>Brainstorming a Tech Odyssey</h2>
      <ul>
        <li>Let's ignite our creativity with a brainstorm! Imagine an adventure into the tech-infused unknown.</li>
        <li>First, explore a galaxy of intriguing theses <a href="http://95theses.co.uk/">here</a>.</li>
        <li>Assemble a crew of the most captivating theses that resonate with your inner explorer.</li>
        <li>Equip each thesis with five keywords that will be your guiding stars on this journey.</li>
        <li>Choose your North Star, the topic that calls out to you the most, and let's set sail.</li>
        <li>Now, unleash your inner cartographer and craft a mind map inspired by your chosen thesis.</li>
      </ul>
    </section>

    <!-- Mind Map #1: Navigating the Uncharted Territories -->
    <section>
      <h2>Mind Map #1: Navigating the Uncharted Territories</h2>
      <img src="../../assets/projects/smartmobile/mindmap.jpg" alt="Mind Map">
    </section>

    <!-- Defining the Problem: Charting the Course -->
    <section>
      <h2>Defining the Problem: Charting the Course</h2>
      <p>
        As we set sail on this technological odyssey, let's transform our curiosity into a focused quest. We begin by translating our list of questions into a carefully curated shortlist. These questions will serve as our guiding stars on this adventure.
      </p>
      <p>
        Now, embark on a digital treasure hunt! Dive deep into the digital seas, unearthing knowledge about your chosen topic. These treasures will shape our grand design challenge, setting the course for our voyage.
      </p>
      <p>
        <strong>Our Destination:</strong> Prepare to be amazed by our vision—an innovative lifestyle app designed for real-time blood sugar level monitoring. This app effortlessly syncs with your Fitbit, detects your exercise routines, allows you to log your dietary choices, and weaves all this data into a comprehensive PDF report for your healthcare provider.
      </p>
    </section>

    <!-- User Description: Our Fellow Travelers -->
    <section>
      <h2>User Description: Our Fellow Travelers</h2>
      <p>
        <strong>Meet Our Fellow Travelers:</strong> Our primary audience, the brave souls living with diabetes.
      </p>
      <p>
        <strong>The Challenges They Face:</strong> Navigating the turbulent waters of managing blood sugar levels and undergoing frequent testing (3 to 5 times a day).
      </p>
      <p>
        <strong>How Can Our Odyssey Help Them?</strong> Brace yourself for the revelation: Our app offers continuous, pain-free blood sugar monitoring, available 24/7, providing respite from the storm.
      </p>
      <p>
        <strong>Challenges on the Horizon:</strong> Keep an eye on the cost; our travelers may require specific hardware, including a compatible chip and Bluetooth sender (and possibly a Fitbit).
      </p>
      <p>
        <strong>How Our Fellow Travelers Communicate:</strong> Our companions are fluent in a variety of languages: email, social media, phone calls, and texts.
      </p>
    </section>
    <br>
    <!-- Week 2: Setting Sail in the World of iOS -->
    <section>
      <h1 >Week 2: Setting Sail in the World of iOS</h1>
      <p>
        Our second week at sea was nothing short of exhilarating! We unfurled the sails, ready to explore the uncharted waters of iOS. The journey led us through sketching the design and features of our app. We had a few tricks up our sleeve, experimenting with Swift and XCode. An old iPhone 4S became our trusted compass on this voyage, allowing us to test our creations in the real world.
      </p>
      <p>
        The adventure continued with a workshop on iOS storyboards, where we learned to navigate the high seas of app design. But that wasn't all—tutorials beckoned, providing further insights into this exciting realm.
        <a href="https://www.raywenderlich.com/464-storyboards-tutorial-for-ios-part-1">Part 1</a>,
        <a href="https://www.raywenderlich.com/462-storyboards-tutorial-for-ios-part-2">Part 2</a>.
      </p> <br>
      <div class="columns is-multiline">
        <div class="column is-full">
          <img src="../../assets/projects/smartmobile/prototype1.jpg" alt="App Prototype 1">
        </div>
      </div>
      <br>
    </section>

    <!-- Week 3: Navigating the Tides of User-Centric Design -->
    <section>
      <h1 >Week 3: Navigating the Tides of User-Centric Design</h1>
      <p>
        While week three presented a unique challenge—my P-graduation—I was not deterred. I embarked on a solo quest, uncovering the treasure trove of knowledge with a workshop about fetching data from a server using JQuery and mastering the art of data handling. This knowledge would prove invaluable on our journey.
      </p>
      <p>
        The week wasn't all solo endeavors, though. Armed with Adobe XD, we crafted our app design, a creation of pure genius. To ensure its success, we subjected it to the ultimate test: user feedback from fellow students. The results were nothing short of magical.
      </p>
      <p>
        Even my diabetic mom, a true hero of our voyage, found the app intuitive. With our target audience's approval, we had the wind at our backs.
      </p> <br>
      <img src="../../assets/projects/smartmobile/iosstoryboards.jpg" alt="iOS Storyboards"> 
      <br> <br>
    </section>

    <!-- Week 4: Focusing on UX and Refining Our Compass -->
    <section>
      <h1 >Week 4: Focusing on UX and Refining Our Compass</h1>
      <p>
        This week's adventure began with a workshop dedicated to the art of User Experience (UX). We immersed ourselves in usability tests and the ten sacred Heuristics, further sharpening our navigational skills.
      </p>
      <p>
        The quest continued with essential tutorials, each a treasure in itself:
        <ul>
          <li>Swift 4 & iOS 11: Custom Camera View (Parts 1, 2, 3, and 4)</li>
          <li>SMTE Workshop 2 UI</li>
          <li>SMTE Workshop 3 JQuery (1/2)</li>
        </ul>
      </p>
      <p>
        While a few challenges loomed on the horizon, we remain steadfast. These hurdles are but stepping stones in our epic voyage.
      </p>

    </section>

    <!-- Week 5: Embracing the Vibrant World of Android -->
    <section>
      <h1 >Week 5: Embracing the Vibrant World of Android</h1>
      <p>
        The fifth week of our grand adventure witnessed a pivotal decision. Our project idea was presented and discussed with our esteemed teachers, leading to a momentous change. With unanimous enthusiasm, we redirected our compass towards the captivating realm of Android development.
      </p>
      <p>
        The journey shifted as we began crafting an app capable of receiving data from Arduino, a crucial piece of our technological puzzle. We also ventured into the uncharted territories of graph research, mastering their integration into the heart of Android Studio.
      </p>
      <p>
        Along the way, we created vital Arduino scripts, each a treasure in itself:
        <ul>
          <li>Sending data over Bluetooth</li>
          <li>Retrieving an analog value from a potentiometer</li>
        </ul>
      </p>
      <p>
        Our epic voyage continues, with promises of combining these elements to craft a basic app featuring dynamic graph visualization in the coming weeks. The adventure is far from over, and the thrill is only intensifying!
      </p>
      <br>
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-third is-vcentered">
            <strong>Android App Development 1:</strong>
            <img src="../../assets/projects/smartmobile/androidapp1.jpg" alt="Android App Development 1">
        </div>
        <div class="column is-one-third is-vcentered">
            <strong>Android App Development 2:</strong>
            <img src="../../assets/projects/smartmobile/androidapp2.jpg" alt="Android App Development 2">
        </div>
        <div class="column is-full is-vcentered">
            <strong>Arduino Sensor:</strong>
            <img src="../../assets/projects/smartmobile/arduinosender.jpg" alt="Arduino Sensor">
        </div>
    </div> <br>
    </section>
    <!-- Group Project: Cooler Truck Monitoring App -->
    <section>
      <h1 >Group Project: Cooler Truck Monitoring App</h1>
      <p>
        Throughout the semester, I also had the opportunity to collaborate with a group of three talented students on an exciting project. Our mission was to develop an Android app tailored for cooler truck drivers responsible for transporting sensitive cargo like medication and food. The app's primary features included real-time temperature monitoring and location tracking without relying on traditional map-based location services.
      </p>
      <p>
        We tackled this challenge by creating a unique solution. Our app allowed cooler truck drivers to check the temperature inside their vehicles with ease. Moreover, we implemented a novel approach to transmit the truck's location to the companies involved in shipping and receiving the products.
      </p>
      <p>
        Instead of using standard mapping services, which were restricted for this assignment, we devised a method to triangulate the truck's location based on nearby telephone poles. This information was then used to send concise location updates over a 3G connection. It was an innovative approach that proved to be both effective and in line with the assignment's requirements.
      </p>
      <p>
        But we didn't stop at app development. To demonstrate our solution's effectiveness, we arranged a presentation for the company that supplied the sensors and assigned the project. We showcased our app's functionalities and the clever way we handled location tracking. To top it off, we even used our app to map our journey from school to the company, providing a real-world demonstration of our solution in action.
      </p>
    </section>


      </div>
    </template>


 
<style scoped>
  
@import 'project.css';

</style>